import dayjs from 'dayjs';

import type { PrintLetterhead } from '../api/bookings';
import type { CashDayBook } from '../api/cash';
import type { TaxDocument } from '../api/taxDocuments';
import { liveGarments } from '../bookings/discountForm';
import { paymentKindLabel, paymentMethodLabel } from '../bookings/paymentWords';
import { bookingReference, referenceLabel } from '../bookings/reference';
import { formatDocumentDate } from '../format/dates';
import { formatMoney } from '../format/money';
import { encodeQr, fits } from '../format/qr';
import { documentTitle, lineNote, orderedLines, taxColumns } from '../invoices/register';
import type { Statement } from '../outstanding/statement';
import { outcomeOf } from '../cash/count';
import { sourceLabels } from '../cash/position';
import { withReference } from '../units/referenceNumber';

import { amountInWords } from './amountInWords';
import {
  chargeBlock,
  depositBlock,
  moneyPhase,
  returnExposure,
  settledDeposit,
} from './customerMoney';
import { escapeHtml, mmToPt, qrSvg, type PrintPage } from './printHtml';
import { instant, type ReceiptInput, type ReceiptPaper } from './receipt';

/**
 * The A5 and A4 sheets — the "Clean" layout the owner chose on 2026-10-15 (canvas "Print Format
 * Options"): the shop's name in the serif over a rule, plain hairlines, and the figure to hand over in
 * a box.
 *
 * <p>
 * <strong>The rolls are these sheets too</strong> (owner, 2026-10-19: "like A5"): reflowed to 58 or 80 mm,
 * blocks stacked, every colour black, an invoice one block per line, and cut to the length the
 * preview measures. The fixed-column text layouts stay for the Bluetooth printer's text mode.
 * </p>
 *
 * <p>
 * <strong>Why sheets exist beside the roll.</strong> The phone printed everything as a 58 or 80 mm
 * roll, and Android's print dialog ignores a page size it is asked for — so on an ordinary printer
 * the roll came out as a strip on an A4 sheet, differently on every phone. A sheet is laid out for
 * the paper it says, with real columns, so nothing depends on a fixed-width font.
 * </p>
 *
 * <p>
 * <strong>The same document as the roll, block for block.</strong> The figures come from the customer
 * copy's blocks (`customerMoney.ts`) exactly as `receipt.ts` reads them, and keep its rules: the
 * deposit is its own block and never inside the invoice total; damage is never given a figure before
 * inspection; a late fee is left off where no rate is set. The invoice prints only stored fields, as
 * `thermalInvoice.ts` does — the client never recomputes tax.
 * </p>
 */

export type SheetSize = 'A5' | 'A4';

/** Every paper a print screen offers, narrowest first — the server's `PaperSizes.All`. */
export type PaperSize = ReceiptPaper | SheetSize;

export const paperSizes: readonly PaperSize[] = ['58mm', '80mm', 'A5', 'A4'];

export const paperLabel: Record<PaperSize, string> = {
  '58mm': '58 mm roll',
  '80mm': '80 mm roll',
  A5: 'A5',
  A4: 'A4',
};

export const isRoll = (paper: PaperSize): paper is ReceiptPaper =>
  paper === '58mm' || paper === '80mm';

/** A stored or received value read back safely — anything unknown is the product's A5. */
export function paperOf(value: string | null | undefined): PaperSize {
  return (paperSizes as readonly string[]).includes(value ?? '') ? (value as PaperSize) : 'A5';
}

const sheetMm: Record<SheetSize, { width: number; height: number; margin: number; base: number }> =
  {
    // Half a sheet holds the same blocks at a slightly smaller type and margin.
    A5: { width: 148, height: 210, margin: 10, base: 8.6 },
    A4: { width: 210, height: 297, margin: 14, base: 10 },
  };

/**
 * The rolls carry the same layout as the sheets (owner, 2026-10-19: "like A5"), reflowed to the
 * roll's width. The margin is padding inside the page, because a thermal driver ignores page margins
 * and its head cannot reach the paper's edge.
 */
const rollMm: Record<ReceiptPaper, { width: number; margin: number; base: number }> = {
  '80mm': { width: 80, margin: 4, base: 8 },
  '58mm': { width: 58, margin: 3, base: 7 },
};

/**
 * How long a roll is cut before the preview has measured it. The phone's preview reports the real
 * length (`rollLengthScript`) and the page is rebuilt to it, so this only ever stands for a moment.
 */
export const rollLengthGuessMm = 200;

/**
 * Run in the preview's web view: posts the page's drawn height in millimetres, so the roll is cut
 * where its content ends. The page is drawn at the roll's own width in CSS pixels (96 to the inch).
 */
export const rollLengthScript =
  'window.ReactNativeWebView && window.ReactNativeWebView.postMessage(String(document.body.scrollHeight * 25.4 / 96)); true;';

/** The roll length a preview reported, made safe: a number, never shorter than a slip. */
export function rollLengthOf(message: string): number | null {
  const mm = Number(message);

  return Number.isFinite(mm) && mm > 0 ? Math.max(40, Math.ceil(mm) + 2) : null;
}

/**
 * A thermal head burns a dot or does not — there is no grey, so every muted colour and hairline
 * becomes black or it dithers into a stipple. Side-by-side blocks stack: 72 mm has no room for two.
 */
const rollCss = `
    body, .muted, .cap, .note, .scan, .shop, .fig, th { color: #000; }
    .top { border-bottom-color: #000; flex-direction: column; align-items: stretch; gap: 4pt; }
    .right { text-align: left; }
    .grid, .cols { flex-direction: column; gap: 6pt; }
    .row { border-bottom: 0.5pt dashed #000; }
    td { border-bottom: 0.5pt dashed #000; }
    .box { border-color: #000; }
    .foot { flex-direction: column; align-items: stretch; gap: 8pt; }
    .scan .qr { width: 30mm; height: 30mm; }
    .sign { min-width: 0; }`;

function sheet(
  paper: PaperSize,
  title: string,
  body: string,
  rollLengthMm = rollLengthGuessMm,
): PrintPage {
  const roll = isRoll(paper);
  const { width, height, margin, base } = roll
    ? { ...rollMm[paper], height: rollLengthMm }
    : sheetMm[paper];
  const size = roll ? `${width}mm ${height}mm` : paper;

  const css = `
    @page { size: ${size}; margin: ${roll ? 0 : margin}mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { color: #1f2933; font-family: 'Noto Sans', 'Segoe UI', Roboto, Arial, sans-serif;
           font-size: ${base}pt; line-height: 1.4; }
    .top { display: flex; justify-content: space-between; align-items: flex-end; gap: 12pt;
           border-bottom: 1.5pt solid #083344; padding-bottom: 6pt; margin-bottom: 10pt; }
    .shop { font-family: 'Playfair Display', Georgia, 'Noto Serif', serif; font-weight: 600;
            font-size: ${(base * 2).toFixed(1)}pt; color: #083344; line-height: 1.15; }
    .muted { color: #5b6570; }
    .cap { font-size: ${(base * 0.78).toFixed(1)}pt; letter-spacing: 1.2pt; text-transform: uppercase;
           color: #5b6570; margin-bottom: 2pt; }
    .ref { font-weight: 700; font-size: ${(base * 1.3).toFixed(1)}pt; }
    .right { text-align: right; }
    .grid { display: flex; gap: 14pt; margin-bottom: 10pt; }
    .grid > div { flex: 1; min-width: 0; }
    .strong { font-weight: 700; }
    .block { margin-bottom: 10pt; break-inside: avoid; }
    .row { display: flex; justify-content: space-between; gap: 10pt; padding: 3pt 0;
           border-bottom: 0.5pt solid #dfe3e8; }
    .row > span:last-child { white-space: nowrap; font-variant-numeric: tabular-nums; text-align: right; }
    .row.total { font-weight: 700; border-bottom: 0; }
    .cols { display: flex; gap: 18pt; }
    .cols > div { flex: 1; min-width: 0; }
    .box { border: 1.5pt solid #083344; border-radius: 6pt; padding: 8pt 10pt; display: flex;
           justify-content: space-between; align-items: center; gap: 10pt; break-inside: avoid;
           margin-bottom: 10pt; }
    .fig { font-family: 'Playfair Display', Georgia, 'Noto Serif', serif; font-weight: 600;
           font-size: ${(base * 2.2).toFixed(1)}pt; color: #083344; white-space: nowrap; }
    .note { font-size: ${(base * 0.9).toFixed(1)}pt; color: #5b6570; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 10pt; }
    th { text-align: left; font-size: ${(base * 0.78).toFixed(1)}pt; letter-spacing: 0.8pt;
         text-transform: uppercase; color: #5b6570; border-bottom: 1pt solid #1f2933; padding: 3pt 4pt; }
    td { padding: 4pt; border-bottom: 0.5pt solid #dfe3e8; vertical-align: top; }
    .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .foot { display: flex; justify-content: space-between; align-items: flex-end; gap: 14pt;
            margin-top: 16pt; font-size: ${(base * 0.9).toFixed(1)}pt; break-inside: avoid; }
    .scan { text-align: center; font-size: ${(base * 0.85).toFixed(1)}pt; color: #5b6570; }
    .scan .qr { width: 22mm; height: 22mm; display: block; margin: 0 auto 1pt; }
    .sign { border-top: 0.75pt solid #1f2933; min-width: 42mm; text-align: center; padding-top: 3pt; }
    ${roll ? `body { width: ${width}mm; padding: ${margin}mm; }${rollCss}` : `@media screen { body { width: ${width}mm; padding: ${margin}mm; } }`}`;

  // On screen — the phone's preview — the page is drawn at the sheet's own width with its margins,
  // and the viewport is that width, so the preview shrinks the whole sheet to fit rather than
  // reflowing it to the phone. Print ignores both and uses @page.
  const viewportPx = Math.round((width / 25.4) * 96);

  return {
    html: `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=${viewportPx}"><title>${escapeHtml(title)}</title><style>${css}</style></head><body>${body}</body></html>`,
    widthPt: mmToPt(width),
    heightPt: mmToPt(height),
  };
}

const e = escapeHtml;
const row = (label: string, value: string, total = false) =>
  `<div class="row${total ? ' total' : ''}"><span>${e(label)}</span><span>${e(value)}</span></div>`;

/* ------------------------------------------------------- receipt and summary */

/**
 * The payment receipt (with a payment) or the booking summary (without one) on a sheet — the roll's
 * `receiptLines`, block for block.
 */
export function receiptSheet(
  input: Omit<ReceiptInput, 'paper'>,
  size: PaperSize,
  rollLengthMm?: number,
): PrintPage {
  const { booking, letterhead, payment } = input;
  const symbol = letterhead.currencySymbol;
  const money = (amount: number) => `${symbol}${formatMoney(amount)}`;
  const parts: string[] = [];

  const all = payment ? [payment, ...(input.together ?? [])] : [];
  const refund = payment !== null && payment !== undefined && payment.amount < 0;
  const title = payment ? (refund ? 'Refund receipt' : 'Payment receipt') : 'Booking summary';
  const reference = payment
    ? all.map((taken) => taken.receiptNumber).join(', ')
    : referenceLabel(booking);

  /* The shop, and what this paper is. */
  const shopLines = [
    letterhead.branchName,
    ...letterhead.addressLines,
    letterhead.phone ? `Ph: ${letterhead.phone}` : '',
    letterhead.gstin ? `GSTIN: ${letterhead.gstin}` : '',
  ].filter(Boolean);

  parts.push(
    `<div class="top"><div><div class="shop">${e(letterhead.tenantName)}</div><div class="muted">${e(shopLines.join(' · '))}</div></div>` +
      `<div class="right"><div class="cap">${e(title)}</div><div class="ref">${e(reference)}</div></div></div>`,
  );

  /* Who and when. */
  const phase = moneyPhase(booking);
  const exposure = returnExposure(booking);
  const facts = [
    `<div><div class="cap">Customer</div><div class="strong">${e(booking.customerName)}</div><div class="muted">${e(booking.customerMobile)}</div></div>`,
    `<div><div class="cap">Booking</div><div class="strong">${e(referenceLabel(booking))}</div><div class="muted">${e(`${formatDocumentDate(booking.possessionFromDate)} to ${formatDocumentDate(booking.possessionToDate)}`)}</div></div>`,
  ];

  if (phase !== 'settled') {
    const late =
      exposure.lateFeePerDay !== null ? `Late: ${money(exposure.lateFeePerDay)} a day` : '';
    facts.push(
      `<div><div class="cap">Return by</div><div class="strong">${e(formatDocumentDate(booking.possessionToDate))}</div><div class="muted">${e(late)}</div></div>`,
    );
  }

  parts.push(`<div class="grid">${facts.join('')}</div>`);

  /* The figure handed over, for a receipt. */
  if (payment) {
    const figure = Math.abs(all.reduce((sum, taken) => sum + taken.amount, 0));
    const what =
      all.length === 1
        ? paymentKindLabel[payment.kind]
        : all
            .map((taken) => `${paymentKindLabel[taken.kind]} ${money(Math.abs(taken.amount))}`)
            .join(' + ');
    const how = [
      paymentMethodLabel[payment.method],
      payment.reference ? `Ref ${payment.reference}` : '',
      instant(payment.receivedOn),
    ].filter(Boolean);

    parts.push(
      `<div class="box"><div><div class="cap">${refund ? 'Paid back' : 'Received with thanks'}</div>` +
        `<div class="note">${e(amountInWords(figure))}</div><div class="note">${e(`${what} · ${how.join(' · ')}`)}</div></div>` +
        `<div class="fig">${e(money(figure))}</div></div>`,
    );
  }

  /* What is booked. */
  const garments = liveGarments(booking);

  if (garments.length > 0) {
    parts.push(
      `<div class="block"><div class="cap">What is booked</div>${garments
        .map((garment) =>
          row(
            [
              garment.itemName,
              garment.variantDisplayName,
              withReference(garment.serialCode, garment.referenceNumber),
            ]
              .filter(Boolean)
              .join(' · '),
            garment.rentalRate === null ? '' : money(garment.rentalRate),
          ),
        )
        .join('')}</div>`,
    );
  }

  /* The rent and the deposit, side by side — never one inside the other. */
  const charges = chargeBlock(booking);
  const rent: string[] = charges.lines.map((line) =>
    row(line.label, `${line.direction === 'less' ? '− ' : ''}${money(line.amount)}`),
  );
  rent.push(row('Invoice total', money(charges.invoiceTotal), true));

  for (const receipt of charges.receipts) {
    rent.push(row(receipt.label, `${receipt.givenBack ? '+ ' : '− '}${money(receipt.amount)}`));
  }

  rent.push(
    charges.balance > 0
      ? row(
          phase === 'beforePickup' ? 'Balance to pay' : 'Still to pay',
          money(charges.balance),
          true,
        )
      : row('Balance', 'Paid', true),
  );

  if (charges.alterationsAbsorbed) {
    rent.push(`<div class="note">Some alteration was done free of charge.</div>`);
  }

  const settled = settledDeposit(booking);
  const deposit = depositBlock(booking);
  const held: string[] = [];

  if (settled) {
    held.push(row('Held', money(settled.held)));

    for (const deduction of settled.named) {
      held.push(
        row(
          deduction.wearerName ? `${deduction.label} · ${deduction.wearerName}` : deduction.label,
          `− ${money(deduction.amount)}`,
        ),
      );
    }

    if (settled.unnamed > 0) {
      held.push(row('Late fee and other charges', `− ${money(settled.unnamed)}`));
    }

    if (settled.towardsRent > 0) {
      held.push(row('Towards the rent owed', `− ${money(settled.towardsRent)}`));
    }

    held.push(row('Refunded to you', money(settled.refunded), true));
  } else if (deposit.onHold > 0) {
    if (deposit.due > 0) {
      held.push(row('To pay', money(deposit.due)));
    }

    if (deposit.held > 0) {
      held.push(row(deposit.due > 0 ? 'Already with us' : 'Held', money(deposit.held)));
    }

    held.push(row('Refundable', money(deposit.onHold), true));
    held.push(
      `<div class="note">Not part of the bill. Given back after the garments come back, less any charges.</div>`,
    );
  }

  parts.push(
    `<div class="cols block"><div><div class="cap">The rent</div>${rent.join('')}</div>` +
      (held.length > 0
        ? `<div><div class="cap">${settled ? 'Deposit settled' : 'Security deposit'}</div>${held.join('')}</div>`
        : '') +
      `</div>`,
  );

  /* What to hand over at pickup, added up where it can be checked. */
  if (phase === 'beforePickup' && (charges.balance > 0 || deposit.due > 0)) {
    const sum = [
      charges.balance > 0 ? `balance ${money(charges.balance)}` : '',
      deposit.due > 0 ? `deposit ${money(deposit.due)}` : '',
    ].filter(Boolean);

    parts.push(
      `<div class="box"><div><div class="cap">To hand over at pickup</div><div class="note">${e(sum.join(' + '))}</div></div>` +
        `<div class="fig">${e(money(Math.max(0, charges.balance) + deposit.due))}</div></div>`,
    );
  }

  /* The terms — before the return only. Damage is never a figure before inspection. */
  if (phase !== 'settled') {
    const terms: string[] = [];

    for (const accessory of exposure.accessories) {
      terms.push(
        row(
          `${accessory.name}${accessory.quantity > 1 ? ` × ${accessory.quantity}` : ''} not returned`,
          money(accessory.total),
        ),
      );
    }

    terms.push(row('Damage', 'assessed at inspection'));
    terms.push(
      `<div class="note">${e(
        exposure.unsecured
          ? 'No deposit is held, so anything charged is payable separately.'
          : 'Deducted from the deposit. Anything above it is payable separately.',
      )}</div>`,
    );
    parts.push(`<div class="block"><div class="cap">On return</div>${terms.join('')}</div>`);
  }

  /* The foot. */
  const by = payment?.receivedByName ? `Received by ${payment.receivedByName} · ` : '';
  // The booking's number as a QR, as on the roll — an enquiry has none to encode.
  const numbered = bookingReference(booking);
  const scan =
    numbered.kind !== 'unnumbered' && fits(numbered.number)
      ? `<div class="scan">${qrSvg(encodeQr(numbered.number))}${e(numbered.number)}</div>`
      : '';
  parts.push(
    `<div class="foot"><div class="muted">${e(`${by}Printed ${instant(input.printedAt)}`)}<br>Thank you</div>` +
      scan +
      `<div class="sign">For ${e(letterhead.tenantName)}</div></div>`,
  );

  return sheet(size, `${title} ${reference}`, parts.join(''), rollLengthMm);
}

/* ------------------------------------------------------------------ invoice */

export interface InvoiceSheetInput {
  document: TaxDocument;
  letterhead: Pick<PrintLetterhead, 'currencySymbol' | 'phone'>;
  printedAt: string;
}

/** A filed tax document on a sheet — only stored fields, as `thermalInvoiceLines` prints them. */
export function invoiceSheet(
  input: InvoiceSheetInput,
  size: PaperSize,
  rollLengthMm?: number,
): PrintPage {
  const { document, letterhead } = input;
  const roll = isRoll(size);
  const money = (amount: number) => `${letterhead.currencySymbol}${formatMoney(amount)}`;
  const columns = taxColumns(document);
  const title = `${documentTitle(document)}${document.status === 'Cancelled' ? ' · cancelled' : ''}`;
  const parts: string[] = [];

  const supplier = [
    document.supplierAddress ?? '',
    document.supplierGstin ? `GSTIN: ${document.supplierGstin}` : '',
    document.supplierStateCode ? `State code: ${document.supplierStateCode}` : '',
    letterhead.phone ? `Ph: ${letterhead.phone}` : '',
  ].filter(Boolean);

  parts.push(
    `<div class="top"><div><div class="shop">${e(document.supplierName)}</div><div class="muted">${e(supplier.join(' · '))}</div></div>` +
      `<div class="right"><div class="cap">${e(title)}</div><div class="ref">${e(document.documentNumber)}</div><div class="muted">${e(formatDocumentDate(document.documentDate))}</div></div></div>`,
  );

  const about = [
    document.bookingNumber ? `Booking ${document.bookingNumber}` : '',
    document.againstDocumentNumber ? `Against ${document.againstDocumentNumber}` : '',
    document.placeOfSupply
      ? `Place of supply ${document.placeOfSupply}${document.isInterState ? ' (inter-state)' : ''}`
      : '',
  ].filter(Boolean);
  const customer = [
    document.customerGstin ? `GSTIN: ${document.customerGstin}` : '',
    document.customerAddress ?? '',
    document.customerMobile ? `Mobile: ${document.customerMobile}` : '',
  ].filter(Boolean);

  parts.push(
    `<div class="grid"><div><div class="cap">Bill to</div><div class="strong">${e(document.customerName)}</div>${customer
      .map((line) => `<div class="muted">${e(line)}</div>`)
      .join(
        '',
      )}</div><div class="right">${about.map((line) => `<div class="muted">${e(line)}</div>`).join('')}</div></div>`,
  );

  /* The lines, in the order the document was built. */
  const taxed = columns !== 'none';
  const head = `<tr><th>Description</th><th>SAC / HSN</th><th class="num">Qty</th><th class="num">Rate</th><th class="num">Taxable value</th>${taxed ? '<th class="num">GST</th>' : ''}</tr>`;
  const body = orderedLines(document.lines)
    .map((line) => {
      const note = lineNote(line, money);
      const code = line.sacCode ?? line.hsnCode ?? '';

      return `<tr><td>${e(line.description)}${note ? `<div class="note">${e(note)}</div>` : ''}</td><td>${e(code)}</td><td class="num">${line.quantity}</td><td class="num">${e(money(line.unitValue))}</td><td class="num">${e(money(line.taxableValue))}</td>${taxed ? `<td class="num">${line.ratePct}%</td>` : ''}</tr>`;
    })
    .join('');

  // Six columns do not fit a roll: each line becomes its description over one figure row.
  const stacked = orderedLines(document.lines)
    .map((line) => {
      const note = lineNote(line, money);
      const code = line.sacCode ?? line.hsnCode ?? '';
      const detail = [
        `${line.quantity} × ${money(line.unitValue)}`,
        code ? `SAC/HSN ${code}` : '',
        taxed ? `GST ${line.ratePct}%` : '',
      ].filter(Boolean);

      return `<div class="block"><div class="strong">${e(line.description)}</div>${note ? `<div class="note">${e(note)}</div>` : ''}${row(detail.join(' · '), money(line.taxableValue))}</div>`;
    })
    .join('');

  parts.push(
    roll
      ? `<div class="cap">Description · taxable value</div>${stacked}`
      : `<table><thead>${head}</thead><tbody>${body}</tbody></table>`,
  );

  /* The totals — every one a stored field. */
  const totals = [row('Taxable value', money(document.taxableValue))];

  if (columns === 'cgst-sgst') {
    totals.push(row('CGST', money(document.cgstAmount)), row('SGST', money(document.sgstAmount)));
  }

  if (columns === 'igst') {
    totals.push(row('IGST', money(document.igstAmount)));
  }

  if (document.roundOff !== 0) {
    totals.push(row('Round off', money(document.roundOff)));
  }

  parts.push(
    `<div class="cols block"><div><div class="cap">Amount in words</div><div class="strong">${e(amountInWords(document.documentTotal))}</div>` +
      `<div class="note" style="margin-top: 6pt">Goods above are hired, not sold. Any security deposit is not part of this invoice.</div></div>` +
      `<div>${totals.join('')}</div></div>`,
  );

  parts.push(
    `<div class="box"><div class="cap">${e(documentTitle(document))} total</div><div class="fig">${e(money(document.documentTotal))}</div></div>`,
  );

  /* Tax by rate — only when more than one rate is on the document. */
  if (taxed && document.rateSummary.length > 1) {
    const rateHead = `<tr><th>Rate</th><th class="num">Taxable value</th>${columns === 'igst' ? '<th class="num">IGST</th>' : '<th class="num">CGST</th><th class="num">SGST</th>'}</tr>`;
    const rateBody = document.rateSummary
      .map(
        (summary) =>
          `<tr><td>${summary.ratePct}%</td><td class="num">${e(money(summary.taxableValue))}</td>${
            columns === 'igst'
              ? `<td class="num">${e(money(summary.igstAmount))}</td>`
              : `<td class="num">${e(money(summary.cgstAmount))}</td><td class="num">${e(money(summary.sgstAmount))}</td>`
          }</tr>`,
      )
      .join('');

    const rateRows = document.rateSummary
      .map(
        (summary) =>
          `<div class="block"><div class="strong">GST ${summary.ratePct}%</div>${row('Taxable value', money(summary.taxableValue))}${
            columns === 'igst'
              ? row('IGST', money(summary.igstAmount))
              : row('CGST', money(summary.cgstAmount)) + row('SGST', money(summary.sgstAmount))
          }</div>`,
      )
      .join('');

    parts.push(
      roll
        ? `<div class="cap">Tax by rate</div>${rateRows}`
        : `<div class="cap">Tax by rate</div><table><thead>${rateHead}</thead><tbody>${rateBody}</tbody></table>`,
    );
  }

  const notes = [
    document.isBillOfSupply ? 'Bill of supply — not eligible for input tax credit.' : '',
    document.reason ?? '',
  ].filter(Boolean);

  for (const note of notes) {
    parts.push(`<div class="note block">${e(note)}</div>`);
  }

  parts.push(
    `<div class="foot"><div class="muted">${e(`Printed ${instant(input.printedAt)}`)}</div>` +
      `<div class="sign">For ${e(document.supplierName)}<br>Authorised signatory</div></div>`,
  );

  return sheet(size, `${title} ${document.documentNumber}`, parts.join(''), rollLengthMm);
}

/* ------------------------------------------------------------------- statement */

/** One party's statement, with the shop it comes from — `outstanding/statement.ts` shapes it. */
export type StatementSheetInput = Statement & { letterhead: PrintLetterhead };

/**
 * A statement (Outstanding, Option A, 2026-10-22): the shop, who it is to, what is still owed on each
 * document with its date and age, the age bands and the total. Every figure is the list's — the
 * server's — and nothing is worked out here.
 */
export function statementSheet(
  input: StatementSheetInput,
  size: PaperSize,
  rollLengthMm?: number,
): PrintPage {
  const { letterhead } = input;
  const money = (amount: number) => `${letterhead.currencySymbol}${formatMoney(amount)}`;
  const shopLines = [
    letterhead.branchName,
    ...letterhead.addressLines,
    letterhead.phone ? `Ph: ${letterhead.phone}` : '',
    letterhead.gstin ? `GSTIN: ${letterhead.gstin}` : '',
  ].filter(Boolean);

  const parts = [
    `<div class="top"><div><div class="shop">${e(letterhead.tenantName)}</div><div class="muted">${e(shopLines.join(' · '))}</div></div>` +
      `<div class="right"><div class="cap">Statement</div><div class="ref">${e(formatDocumentDate(input.asOn))}</div></div></div>`,
    `<div class="block"><div class="cap">To</div><div class="strong">${e(input.party.name)}</div>${input.party.mobile ? `<div class="muted">${e(input.party.mobile)}</div>` : ''}</div>`,
    `<table><thead><tr><th>Document</th><th>${e(input.dateLabel)}</th><th class="num">Age</th><th class="num">Still owed</th></tr></thead><tbody>` +
      input.lines
        .map(
          (line) =>
            `<tr><td><span class="strong">${e(line.reference)}</span><br><span class="muted">${e(line.what)}</span></td><td>${e(formatDocumentDate(line.date))}</td><td class="num">${e(line.age)}</td><td class="num">${e(money(line.amount))}</td></tr>`,
        )
        .join('') +
      `</tbody></table>`,
    `<div class="cols"><div></div><div>${input.bands
      .map((band) => row(band.label, band.amount === 0 ? '—' : money(band.amount)))
      .join('')}</div></div>`,
    `<div class="box"><span class="strong">Total due</span><span class="fig">${e(money(input.total))}</span></div>`,
    input.onAccount
      ? `<div class="note">${e(`${money(input.onAccount)} of ours is on account with them.`)}</div>`
      : '',
    input.closing ? `<div class="note">${e(input.closing)}</div>` : '',
  ];

  return sheet(size, `Statement ${input.party.name}`, parts.join(''), rollLengthMm);
}

/* -------------------------------------------------------------------- day book */

/** The day book for one day — one section per account, each beginning a page. */
export interface DayBookSheetInput {
  letterhead: PrintLetterhead;
  date: string;
  books: readonly CashDayBook[];
  printedAt: string;
}

/**
 * The day book (Cash & bank step 8, `docs/Cash_Bank_Flow_Design.md` §5): for each account the day's
 * opening, every movement by the time it was written with the balance after it, in and out, the
 * closing — "should be in the till" for cash — and the day's count with who counted it, then the two
 * signatures. It is the one page the owner signs every night.
 *
 * <p>
 * <strong>Every figure is the server's</strong> (`GET …/day-book`, the position's own day): the
 * running balance included, so the page cannot disagree with the cash book printed beside it. The
 * rolls stack each row as two lines instead of a table, as the other rolls do.
 * </p>
 */
export function dayBookSheet(
  input: DayBookSheetInput,
  size: PaperSize,
  rollLengthMm?: number,
): PrintPage {
  const { letterhead } = input;
  const roll = isRoll(size);
  const money = (amount: number) =>
    `${amount < 0 ? '−' : ''}${letterhead.currencySymbol}${formatMoney(Math.abs(amount))}`;
  const time = (value: string) => {
    const parsed = dayjs(value);

    return parsed.isValid() ? parsed.format('HH:mm') : '';
  };
  const shopLines = [
    letterhead.branchName,
    ...letterhead.addressLines,
    letterhead.phone ? `Ph: ${letterhead.phone}` : '',
  ].filter(Boolean);

  // An account with nothing carried in, nothing moved, nothing held and no count had no day — one
  // opened since, typically — and a blank page for it is noise on the sheet the owner signs. If every
  // account was empty, the first still prints, saying nothing moved.
  const busy = input.books.filter(
    (book) =>
      book.rows.length > 0 || book.opening !== 0 || book.closing !== 0 || book.count !== null,
  );
  const books = busy.length > 0 ? busy : input.books.slice(0, 1);

  const sections = books.map((book, index) => {
    const cash = book.kind !== 'Bank';
    const what = (row: CashDayBook['rows'][number]) =>
      [sourceLabels[row.sourceKind], row.notes?.trim() || null].filter(Boolean).join(' · ');
    const paper = (row: CashDayBook['rows'][number]) =>
      [row.sourceDocumentNumber || null, row.reference, paymentMethodLabel[row.instrument]]
        .filter(Boolean)
        .join(' · ');

    const rows = roll
      ? book.rows
          .map(
            (row) =>
              `<div class="block"><div class="row"><span>${e(`${time(row.recordedAt)} ${what(row)}`)}</span><span>${e(
                `${row.direction === 'In' ? '+' : '−'}${money(row.amount)}`,
              )}</span></div><div class="muted">${e(`${paper(row)} · balance ${money(row.balance)}`)}</div></div>`,
          )
          .join('')
      : `<table><thead><tr><th>Time</th><th>What</th><th class="num">In</th><th class="num">Out</th><th class="num">Balance</th></tr></thead><tbody>` +
        `<tr><td></td><td class="muted">Carried in</td><td></td><td></td><td class="num">${e(money(book.opening))}</td></tr>` +
        book.rows
          .map(
            (row) =>
              `<tr><td>${e(time(row.recordedAt))}</td><td><span class="strong">${e(what(row))}</span><br><span class="muted">${e(paper(row))}</span></td>` +
              `<td class="num">${row.direction === 'In' ? e(money(row.amount)) : ''}</td><td class="num">${row.direction === 'Out' ? e(money(row.amount)) : ''}</td>` +
              `<td class="num">${e(money(row.balance))}</td></tr>`,
          )
          .join('') +
        `</tbody></table>`;

    const count = book.count;
    const outcome = count ? outcomeOf(count.variance) : null;
    const countBlock = !cash
      ? ''
      : count
        ? `<div class="block"><div class="cap">The count</div>${row('Counted', money(count.counted))}${row('The book said', money(count.expected))}${row(
            outcome === 'agrees' ? 'Difference' : outcome === 'short' ? 'Short by' : 'Over by',
            outcome === 'agrees' ? 'none' : money(Math.abs(count.variance)),
          )}${count.notes ? `<div class="note">${e(count.notes)}</div>` : ''}<div class="note">${e(
            `Counted${count.countedBy ? ` by ${count.countedBy}` : ''} at ${time(count.countedAt)}`,
          )}</div></div>`
        : `<div class="block"><div class="cap">The count</div><div class="note">Not counted on the system. Counted: ${e(letterhead.currencySymbol)} ____________</div></div>`;

    return (
      `<section${index < books.length - 1 ? ' style="break-after: page"' : ''}>` +
      `<div class="top"><div><div class="shop">${e(letterhead.tenantName)}</div><div class="muted">${e(shopLines.join(' · '))}</div></div>` +
      `<div class="right"><div class="cap">Day book</div><div class="ref">${e(formatDocumentDate(book.date))}</div></div></div>` +
      `<div class="block"><div class="strong">${e(book.accountName)}${book.bankReference ? ` · ${e(book.bankReference)}` : ''}</div>` +
      `<div class="muted">${e(cash ? (book.kind === 'Till' ? 'Till' : 'Petty cash') : 'Bank')}</div></div>` +
      (book.hasOpeningBalance
        ? ''
        : `<div class="note block">No opening balance is recorded for this account, so these figures are what moved, not what it holds.</div>`) +
      `<div class="grid"><div>${row('Carried in', money(book.opening))}${row('Money in', money(book.in))}</div><div>${row('Money out', money(book.out))}${row('Carried forward', money(book.closing), true)}</div></div>` +
      (book.rows.length === 0 ? `<div class="note block">Nothing moved on this day.</div>` : rows) +
      `<div class="box"><span class="strong">${e(cash ? 'Should be in hand' : 'Closing balance')}</span><span class="fig">${e(money(book.closing))}</span></div>` +
      countBlock +
      // A bank is matched, not counted, so its first signature is whoever prepared the page.
      `<div class="foot"><div class="sign">${cash ? 'Counted by' : 'Prepared by'}</div><div class="sign">Checked by</div></div>` +
      `<div class="note">${e(`Printed ${instant(input.printedAt)}`)}</div>` +
      `</section>`
    );
  });

  return sheet(
    size,
    `Day book ${formatDocumentDate(input.date)}`,
    sections.join('') || `<div class="note">No account to print.</div>`,
    rollLengthMm,
  );
}
